/**
 * Footer.js v3.5 - ULTRA-KOMPAKT MOBILE
 *
 * ✅ v3.5: ULTRA-KOMPAKT MOBILE
 *         - Nur 60px Höhe auf Mobile
 *         - Einzeilig, keine Wraps
 *         - Links versteckt auf < 380px
 * ✅ v3.3: MOBILE RESPONSIVE FIX
 *         - Kompakter Footer auf Mobile (nur 2 Zeilen)
 *         - Logo und Links überlappen nicht mehr
 *         - Overflow-Problem behoben
 * ✅ v3.2: PREMIUM DESIGN
 *         - Größerer Footer (80px)
 *         - Planivio Teal Gradient Background
 *         - Moderne Optik mit Glassmorphism
 *         - Fixed am unteren Bildschirmrand
 * ✅ v3.1: Fixed Position
 * ✅ v3.0: SVG Logo
 *
 * @version 3.3.0 - Mobile Responsive Fix
 */

import React from 'react';
import { Capacitor } from '@capacitor/core';

// Planivio Logo Component - SVG für transparenten Hintergrund
const PlanivioLogo = ({ size = 24, variant = 'white' }) => {
  const logoSrc = variant === 'white'
    ? '/planivio_logo_white.svg'
    : '/planivio_logo_primary.svg';

  return (
    <img
      src={logoSrc}
      alt="Planivio"
      style={{
        height: `${size}px`,
        width: 'auto',
        flexShrink: 0,
        objectFit: 'contain'
      }}
    />
  );
};

// Footer Höhe für Content-Padding (Desktop: 80px, Mobile: 50px via CSS)
export const FOOTER_HEIGHT = 80;

function Footer({ isDark }) {

  const currentYear = new Date().getFullYear();
  const isNative = Capacitor.isNativePlatform();

  // ===========================
  // ✅ v3.2: PREMIUM STYLES - PLANIVIO TEAL
  // ===========================
  
  const styles = {
    // ✅ v3.2: Premium Gradient Footer
    footer: {
      position: isNative ? 'relative' : 'fixed',
      bottom: isNative ? undefined : 0,
      left: isNative ? undefined : 0,
      right: isNative ? undefined : 0,
      zIndex: 40,
      height: isNative ? 'auto' : `${FOOTER_HEIGHT}px`,
      marginBottom: isNative ? '70px' : undefined,
      // ✅ Planivio Teal Gradient
      background: 'linear-gradient(135deg, #4A90A4 0%, #3D7A8C 50%, #6BA893 100%)',
      padding: '0 1.5rem',
      display: 'flex',
      alignItems: 'center',
      borderTop: '2px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 -4px 20px rgba(74, 144, 164, 0.3)'
    },
    container: {
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    
    // Left Section
    left: {
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem',
      flexWrap: 'wrap'
    },
    brand: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      textDecoration: 'none'
    },
    brandName: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: 'white',
      margin: 0,
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
    },
    copyright: {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: '0.85rem',
      margin: 0,
      fontWeight: 500
    },
    
    // Center Links
    links: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '0.5rem'
    },
    link: {
      color: 'rgba(255, 255, 255, 0.85)',
      textDecoration: 'none',
      fontSize: '0.85rem',
      fontWeight: 500,
      padding: '6px 12px',
      borderRadius: '8px',
      transition: 'all 0.2s',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.15)'
    },
    
    // Right Section
    right: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap'
    },
    madeWith: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '0.85rem',
      fontWeight: 500,
      padding: '6px 12px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    
    // Action Buttons
    actionLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      background: 'rgba(255, 255, 255, 0.95)',
      border: 'none',
      borderRadius: '10px',
      color: '#4A90A4',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.2s',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    },
    inviteLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
      border: 'none',
      borderRadius: '10px',
      color: 'white',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.2s',
      boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)'
    }
  };

  // ===========================
  // FOOTER LINKS
  // ===========================
  
  const footerLinks = [
    { label: 'Datenschutz', href: '/datenschutz' },
    { label: 'AGB', href: '/agb' },
    { label: 'Impressum', href: '/impressum' },
    { label: 'FAQ', href: '/faq' }
  ];

  // ===========================
  // RENDER
  // ===========================

  return (
    <>
      {/* ✅ v3.6: CSS für Mobile - Noch kompakter */}
      <style>{`
        @media (max-width: 768px) {
          .planivio-footer {
            height: 50px !important;
            padding: 0 8px !important;
          }
          .footer-container {
            flex-direction: row !important;
            gap: 6px !important;
            padding: 0 !important;
            justify-content: space-between !important;
          }
          .footer-left {
            flex: 1 !important;
            gap: 6px !important;
            flex-wrap: nowrap !important;
            overflow: hidden !important;
          }
          .footer-brand {
            display: none !important;
          }
          .footer-copyright {
            font-size: 0.6rem !important;
            white-space: nowrap !important;
            opacity: 0.7 !important;
          }
          .footer-links {
            gap: 4px !important;
            flex-wrap: nowrap !important;
          }
          .footer-link {
            padding: 2px 4px !important;
            font-size: 0.55rem !important;
            white-space: nowrap !important;
            background: transparent !important;
            border: none !important;
            opacity: 0.8 !important;
          }
          .footer-right {
            gap: 4px !important;
            flex-wrap: nowrap !important;
            flex-shrink: 0 !important;
          }
          .footer-made-with {
            display: none !important;
          }
          .footer-action {
            padding: 5px 8px !important;
            font-size: 0.65rem !important;
            white-space: nowrap !important;
          }
        }
        @media (max-width: 420px) {
          .footer-links {
            display: none !important;
          }
          .footer-copyright {
            display: none !important;
          }
        }
        @media (max-width: 380px) {
          .footer-action {
            padding: 4px 6px !important;
            font-size: 0.6rem !important;
          }
        }
      `}</style>

      <footer style={styles.footer} className="planivio-footer">
        <div style={styles.container} className="footer-container">

          {/* Left: Brand + Copyright + Links */}
          <div style={styles.left} className="footer-left">
            <a href="/?home=true" style={styles.brand} className="footer-brand">
              <PlanivioLogo size={28} variant="white" />
            </a>

            <span style={styles.copyright} className="footer-copyright">
              © {currentYear} Planivio
            </span>

            {/* Links */}
            <nav style={styles.links} className="footer-links">
              {footerLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  style={styles.link}
                  className="footer-link"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                  }}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Right: Made with + Actions */}
          <div style={styles.right} className="footer-right">
            {/* Made with love - hidden on mobile */}
            <div style={styles.madeWith} className="footer-made-with">
              <span>🇩🇪</span>
              <span>Made with ❤️ in Bavaria</span>
            </div>

            {/* Landing Page Link */}
            <a
              href="/?home=true"
              style={styles.actionLink}
              className="footer-action"
            >
              <span>🌐</span>
              <span>Startseite</span>
            </a>

            {/* Invite Button */}
            <a
              href="/invite"
              style={styles.inviteLink}
              className="footer-action"
            >
              <span>🎁</span>
              <span>Einladen</span>
            </a>
          </div>

        </div>
      </footer>
    </>
  );
}

export default Footer;